/** These question-settings (QuestionConstantT) should be moved into the question modules themselves after it's been double checked which of it's props is actually used(!) */
import type { ModuleId, QuestionType } from '@mentimeter/http-clients';
import {
  PinOnImageIcon,
  QuickFormIcon,
  ChoicesIcon,
  WordcloudIcon,
  OpenEndedIcon,
  ScalesIcon,
  RankingIcon,
  QfaIcon,
  HundredPointsIcon,
  RatingIcon,
  BigIcon,
  BulletsIcon,
  FreeTextIcon,
  HeadingIcon,
  ParagraphIcon,
  ImageIcon,
  VideoIcon,
  QuoteIcon,
  NumberIcon,
  InstructionsIcon,
  DocumentIcon,
  GoogleSlidesIcon,
  PowerpointIcon,
  MiroIcon,
  QuizChoicesIcon,
  QuizOpenIcon,
  QuizLeaderboardIcon,
  GuessTheNumberIcon,
} from '@mentimeter/ragnar-visuals/slide-type-icons';
import keyBy from 'lodash/keyBy';
import type { TranslatableQuestionConstant } from '../../types/question';

const questionTypeChoices: TranslatableQuestionConstant = {
  type: 'choices',
  moduleId: 'choices',
  icon: ChoicesIcon,
};

const questionTypeWordCloud: TranslatableQuestionConstant = {
  type: 'wordcloud',
  moduleId: 'wordcloud',
  icon: WordcloudIcon,
};

const questionTypeOpen: TranslatableQuestionConstant = {
  type: 'open',
  moduleId: 'open',
  icon: OpenEndedIcon,
};

const questionTypeScales: TranslatableQuestionConstant = {
  type: 'scales',
  moduleId: 'scales',
  icon: ScalesIcon,
};

const questionTypeRanking: TranslatableQuestionConstant = {
  type: 'ranking',
  moduleId: 'ranking',
  icon: RankingIcon,
};

const questionTypeQfa: TranslatableQuestionConstant = {
  type: 'qfa',
  moduleId: 'qfa',
  // unrestricted means available on free plan within question limit
  unrestricted: true,
  icon: QfaIcon,
};

const questionTypePrioritisation: TranslatableQuestionConstant = {
  type: 'prioritisation',
  moduleId: '100-points',
  icon: HundredPointsIcon,
};

const questionTypeRating: TranslatableQuestionConstant = {
  type: 'rating',
  moduleId: 'rating',
  icon: RatingIcon,
};

const questionTypePinOnImage: TranslatableQuestionConstant = {
  type: 'pin_on_image',
  moduleId: 'pin-on-image',
  icon: PinOnImageIcon,
};

const questionTypeMetadata: TranslatableQuestionConstant = {
  type: 'metadata',
  moduleId: 'metadata',
  pro: true,
  icon: QuickFormIcon,
};

const slideTypeBullets: TranslatableQuestionConstant = {
  type: 'slide',
  moduleId: 'bullets',
  unrestricted: true,
  slideType: 'bullets',
  icon: BulletsIcon,
};

const slideTypeBig: TranslatableQuestionConstant = {
  type: 'slide',
  moduleId: 'big',
  unrestricted: true,
  slideType: 'big',
  icon: BigIcon,
};

export const INTERACTIVE_SLIDES_TYPES: Array<TranslatableQuestionConstant> = [
  questionTypeChoices,
  questionTypeWordCloud,
  questionTypeOpen,
  questionTypeScales,
  questionTypeRanking,
  questionTypeQfa,
  {
    moduleId: 'numerical-question',
    type: 'scales',
    icon: GuessTheNumberIcon,
  },
  questionTypePrioritisation,
  questionTypeRating,
  questionTypeMetadata,
  questionTypePinOnImage,
];

const QUIZ_OPEN_QUESTION_TYPE: TranslatableQuestionConstant = {
  type: 'quiz_open',
  moduleId: 'quiz-open',
  icon: QuizOpenIcon,
};

const QUIZ_QUESTION_TYPE: TranslatableQuestionConstant = {
  type: 'quiz',
  moduleId: 'quiz-choices',
  icon: QuizChoicesIcon,
};

const QUIZ_LEADERBOARD_TYPE: TranslatableQuestionConstant = {
  type: 'quiz_leaderboard',
  moduleId: 'quiz-leaderboard',
  unrestricted: true,
  icon: QuizLeaderboardIcon,
};

export const RESTRICTED_INTEGRATION_SLIDES_TYPES: Array<TranslatableQuestionConstant> =
  [
    {
      type: 'slide',
      moduleId: 'google-slides',
      icon: GoogleSlidesIcon,
    },
    {
      type: 'slide',
      moduleId: 'powerpoint',
      icon: PowerpointIcon,
    },
  ];

export const INTEGRATION_SLIDES_TYPES: Array<TranslatableQuestionConstant> = [
  ...RESTRICTED_INTEGRATION_SLIDES_TYPES,
  {
    type: 'slide',
    moduleId: 'miro',
    unrestricted: true,
    icon: MiroIcon,
  },
];

export const CONTENT_SLIDES_TYPES: Array<TranslatableQuestionConstant> = [
  {
    type: 'slide',
    moduleId: 'free-text',
    unrestricted: true,
    slideType: 'free-text',
    icon: FreeTextIcon,
  },
  {
    type: 'slide',
    moduleId: 'heading',
    unrestricted: true,
    slideType: 'heading',
    icon: HeadingIcon,
  },
  {
    type: 'slide',
    moduleId: 'paragraph',
    unrestricted: true,
    slideType: 'paragraph',
    icon: ParagraphIcon,
  },
  slideTypeBullets,
  {
    type: 'slide',
    moduleId: 'image',
    unrestricted: true,
    slideType: 'image',
    icon: ImageIcon,
  },
  {
    type: 'slide',
    moduleId: 'video',
    unrestricted: true,
    slideType: 'video',
    icon: VideoIcon,
  },
  slideTypeBig,
  {
    type: 'slide',
    moduleId: 'quote',
    unrestricted: true,
    slideType: 'quote',
    icon: QuoteIcon,
  },
  {
    type: 'slide',
    moduleId: 'number',
    unrestricted: true,
    slideType: 'number',
    icon: NumberIcon,
  },
  {
    type: 'slide',
    moduleId: 'instructions',
    unrestricted: true,
    slideType: 'instructions',
    icon: InstructionsIcon,
  },
  {
    type: 'slide',
    moduleId: 'document',
    unrestricted: true,
    slideType: 'document',
    icon: DocumentIcon,
  },
];

const QUIZ_OPEN_QUESTION_TYPES = [
  QUIZ_QUESTION_TYPE,
  QUIZ_OPEN_QUESTION_TYPE,
  QUIZ_LEADERBOARD_TYPE,
];

const QUIZ_QUESTION_TYPES = [QUIZ_QUESTION_TYPE, QUIZ_LEADERBOARD_TYPE];

export const ALL_QUIZ_QUESTION_TYPES = [
  QUIZ_QUESTION_TYPE,
  QUIZ_OPEN_QUESTION_TYPE,
];

export const ALL_QUIZ_SLIDE_TYPES = [
  ...ALL_QUIZ_QUESTION_TYPES,
  QUIZ_LEADERBOARD_TYPE,
];

export const ALL_QUESTION_TYPES = [
  ...INTERACTIVE_SLIDES_TYPES,
  ...QUIZ_OPEN_QUESTION_TYPES,
  ...QUIZ_QUESTION_TYPES,
  ...CONTENT_SLIDES_TYPES,
  ...INTEGRATION_SLIDES_TYPES,
];

export const QUIZ_SLIDES_TYPES: QuestionType[] = [
  'quiz',
  'quiz_open',
  'quiz_leaderboard',
];

export const ALL_QUESTION_TYPES_BY_MODULE_ID =
  keyBy<TranslatableQuestionConstant>(ALL_QUESTION_TYPES, 'moduleId');

export const ALL_QUESTION_TYPES_BY_TYPE = keyBy<TranslatableQuestionConstant>(
  ALL_QUESTION_TYPES,
  'type',
);

export const CONTENT_SLIDE_MODULE_IDS = CONTENT_SLIDES_TYPES.map<ModuleId>(
  (slide) => slide.moduleId,
);
